import { useCallback, useEffect, useRef, useState } from 'react';

import { ASSISTANT_ERRORS } from '../assistant.constants';
import {
  AssistantEvent,
  AssistantEventType,
  ChatMessage,
  ToolRun,
  ToolStatus,
} from '../assistant.types';
import {
  AssistantRequestError,
  deleteConversation,
  streamAssistantTurn,
} from '../queries/streamAssistant.query';

// Message ids only need to be unique within this page.
let seq = 0;
const nextId = () => {
  seq += 1;
  return `m-${seq}`;
};

/** New conversation per mount — a reused id replays its old history into the model. */
const newConversationId = () => `admin-fe-${crypto.randomUUID()}`;

/**
 * Prefix every turn with the open participant's name — the service resolves the
 * subject by name each turn and its history summary drops identity values.
 */
const onWire = (participantName: string, text: string) =>
  participantName ? `[Participant open in the portal: ${participantName}]\n${text}` : text;

interface Args {
  participantName: string;
}

const useAssistantChat = ({ participantName }: Args) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const conversationId = useRef(newConversationId());
  const abortRef = useRef<AbortController | null>(null);

  // Abandon an in-flight turn if the rep navigates away mid-answer.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;

      const pendingId = nextId();
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'user', text: trimmed },
        { id: pendingId, role: 'assistant', text: '', pending: true, tools: [] },
      ]);
      setBusy(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const patch = (change: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === pendingId ? change(m) : m)));

      const onEvent = (event: AssistantEvent) => {
        if (event.type === AssistantEventType.TOKEN) {
          patch((m) => ({ ...m, text: m.text + event.text, pending: false }));
          return;
        }
        if (event.type === AssistantEventType.ACTION) {
          patch((m) => {
            const tools: ToolRun[] = m.tools || [];
            if (event.status === ToolStatus.STARTED) {
              return { ...m, tools: [...tools, { tool: event.tool, done: false }] };
            }
            // Close the latest open run of this tool (a tool may run twice in a turn).
            const last = tools.reduce(
              (found, t, i) => (t.tool === event.tool && !t.done ? i : found),
              -1
            );
            if (last === -1) return m;
            return {
              ...m,
              tools: tools.map((t, i) => (i === last ? { ...t, done: true } : t)),
            };
          });
          return;
        }
        if (event.type === AssistantEventType.ERROR) {
          patch((m) => ({ ...m, pending: false, error: true, text: event.message }));
        }
        // `done` needs no handling: the stream closing is the end of the turn.
      };

      try {
        await streamAssistantTurn({
          conversationId: conversationId.current,
          text: onWire(participantName, trimmed),
          signal: controller.signal,
          onEvent,
        });
        // An empty turn would otherwise show typing dots forever.
        patch((m) => (m.pending ? { ...m, pending: false, error: true, text: ASSISTANT_ERRORS.NO_RESPONSE } : m));
      } catch (err) {
        if (controller.signal.aborted) return;
        // Pre-stream failure. 401/403 means the service rejected the token
        // (wrong Cognito app client, or not CUSTOM_ADMIN).
        const status = err instanceof AssistantRequestError ? err.status : 0;
        const text =
          status === 401 || status === 403
            ? ASSISTANT_ERRORS.SESSION_REJECTED
            : ASSISTANT_ERRORS.UNREACHABLE;
        patch((m) => ({ ...m, pending: false, error: true, text }));
      } finally {
        abortRef.current = null;
        setBusy(false);
      }
    },
    [busy, participantName]
  );

  /** Clear the visible thread AND the history stored against it server-side. */
  const reset = useCallback(async () => {
    abortRef.current?.abort();
    const previous = conversationId.current;
    conversationId.current = newConversationId();
    setMessages([]);
    setBusy(false);
    try {
      await deleteConversation(previous);
    } catch {
      // Best effort — the new id already isolates the chat.
    }
  }, []);

  return { messages, busy, send, reset };
};

export default useAssistantChat;
